import type { FC, ReactNode } from "react"
import Box from "@mui/material/Box"
import Tooltip from "@mui/material/Tooltip"

import { ARROW_HEIGHT, ARROW_WIDTH, MARKER_BAND_HEIGHT } from "./helpers"

/**
 * A downward triangle over its span, naming what it marks on hover.
 */
const SpanMarkerArrow: FC<{
  label: ReactNode
  offset: number
  color: string
  // Timestamp under the label, where the marker has one.
  caption?: string | null
  hitWidth: number
}> = ({ label, offset, color, caption, hitWidth }) => (
  <Tooltip
    title={
      <>
        <Box component="span" sx={{ display: "block", fontWeight: 600 }}>
          {label}
        </Box>
        {caption && (
          <Box component="span" sx={{ display: "block", mt: 0.25, opacity: 0.75 }}>
            {caption}
          </Box>
        )}
      </>
    }
    placement="top"
    arrow
    // Touch has no hover: without this the label needs a long press.
    enterTouchDelay={0}
    slotProps={{
      popper: {
        modifiers: [
          {
            name: "offset",
            options: { offset: [0, -(MARKER_BAND_HEIGHT + 3 - ARROW_HEIGHT)] },
          },
        ],
      },
      // Same treatment as the map labels, so these read as ours, not MUI's.
      tooltip: {
        sx: {
          bgcolor: "background.paper",
          color: "text.primary",
          fontSize: "0.7rem",
          lineHeight: 1.4,
          px: 1,
          py: 0.5,
          boxShadow: 3,
          border: "1px solid rgba(255,255,255,0.12)",
        },
      },
      arrow: { sx: { color: "background.paper" } },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        bottom: 0,
        left: "50%",
        transform: `translateX(calc(-50% + ${offset}px))`,
        width: hitWidth,
        height: MARKER_BAND_HEIGHT + 3,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        cursor: "help",
      }}
    >
      <Box
        sx={{
          width: 0,
          height: 0,
          borderLeft: `${ARROW_WIDTH / 2}px solid transparent`,
          borderRight: `${ARROW_WIDTH / 2}px solid transparent`,
          borderTop: `${ARROW_HEIGHT}px solid ${color}`,
        }}
      />
    </Box>
  </Tooltip>
)

export default SpanMarkerArrow
